<template>
  <div class="timeslots timeslots-wrapper px-0 mt-3">
    <div v-if="closed" class="text-primary text-center px-1">
      We are closed on {{ hours.dayOfWeek.description }}s.<br/>Please choose another day for a furry-tastic experience!
    </div>
    <div v-else>
      <resource-timeslots-item
        v-for="resource in sortedResources"
        :key="resource.id"
        :resource="resource"
        :time-slots="resourceSlotsMap[resource.id] || []"
        @available-count-change="onAvailableCountChange"/>
      <div v-if="allResourcesEmpty" class="text-primary text-center px-1">
        No available times on this day.<br/>Please choose another day for a furry-tastic experience!
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone'
import { mapState } from 'vuex'
import { SelectedDateMixin } from '@/mixins/Booking/SelectedDateMixin'
import { SiteBookingSettingMixin } from '@/mixins/Booking/SiteBookingSettingMixin'
import { ResourceCacheMixin } from '@/mixins/Booking/ResourceCacheMixin'
import { WorkingHourCacheMixin } from '@/mixins/Booking/WorkingHourCacheMixin'
import { OneOffHourSearchMixin } from '@/mixins/Booking/OneOffHourSearchMixin'
import { AppointmentSearchMixin } from '@/mixins/Booking/AppointmentSearchMixin'
import ResourceTimeSlotsItem from './ResourceTimeSlotsItem.vue'

export default {
  name: 'TimeSlotsPerResource',
  mixins: [SiteBookingSettingMixin, ResourceCacheMixin, WorkingHourCacheMixin, OneOffHourSearchMixin, AppointmentSearchMixin, SelectedDateMixin],
  data () {
    return {
      availableCountMap: {}
    }
  },
  computed: {
    ...mapState('BookingSelectedDate', [
      'selectedDate',
      'localDateString'
    ]),
    ...mapState('BookingUpload', [
      'duration'
    ]),
    ...mapState('TimeZone', [
      'selectedTimeZoneId'
    ]),
    // Required by OneOffHourSearchMixin
    selectedDatesLocal () {
      return {
        fromLocal: this.localDateString ? this.localDateString + 'T00:00:00' : '',
        toLocal: this.localDateString ? this.localDateString + 'T23:59:59' : ''
      }
    },
    dateString () {
      return moment(this.selectedDate).tz(moment.tz.guess()).format('YYYY-MM-DD')
    },
    hours () {
      return this.openingHours ? this.openingHours.find(oh => oh.dayOfWeek.id === this.selectedDate.getDay()) : null
    },
    closed () {
      return this.hours ? this.hours.closed : false
    },
    openingTime () {
      const timeStr = this.hours ? this.hours.openingLocal.split('T')[1] : ''
      return timeStr ? moment.tz(`${this.dateString}T${timeStr}`, this.selectedTimeZoneId) : null
    },
    closingTime () {
      const timeStr = this.hours ? this.hours.closingLocal.split('T')[1] : ''
      return timeStr ? moment.tz(`${this.dateString}T${timeStr}`, this.selectedTimeZoneId) : null
    },
    dayId () {
      return new Date(this.dateString).getUTCDay()
    },
    sortedResources () {
      return [...this.bookableResources].sort((a, b) => a.name.localeCompare(b.name))
    },
    resourceSlotsMap () {
      const map = {}
      for (const resource of this.bookableResources) {
        map[resource.id] = this.computeSlotsForResource(resource)
      }
      return map
    },
    allResourcesEmpty () {
      if (this.bookableResources.length === 0) return false
      return this.bookableResources.every(r => {
        const slots = this.resourceSlotsMap[r.id] || []
        if (slots.length === 0) return true
        // Has slots — wait for conflict checks to emit before declaring empty
        const count = this.availableCountMap[r.id]
        return count !== undefined && count === 0
      })
    }
  },
  watch: {
    resourceSlotsMap () {
      this.availableCountMap = {}
    }
  },
  methods: {
    onAvailableCountChange ({ resourceId, count }) {
      this.$set(this.availableCountMap, resourceId, count)
    },
    // DateSelect sibling already loads oneOffHours for the selected date
    refreshOneOffHours () {
      // do nothing!
    },
    // DateTimeSelect already loads workingHours
    refreshWorkingHours () {
      // do nothing
    },
    resourceEffectiveBounds (resource) {
      // 1. oneOffHour takes precedence over workingHour
      const oneOffHour = this.oneOffHours.find(h => h.resourceId === resource.id)
      if (oneOffHour) {
        if (oneOffHour.dayOff) return { closed: true }
        return {
          closed: false,
          openTimeStr: oneOffHour.startLocal.split('T')[1],
          closeTimeStr: oneOffHour.endLocal.split('T')[1]
        }
      }
      // 2. workingHour for the selected day
      const workingHour = this.workingHours.find(
        wh => wh.resourceId === resource.id && wh.dayOfWeek.id === this.dayId
      )
      if (workingHour) {
        if (workingHour.dayOff) return { closed: true }
        return {
          closed: false,
          openTimeStr: workingHour.startLocal.split('T')[1],
          closeTimeStr: workingHour.endLocal.split('T')[1]
        }
      }
      // 3. No resource hours — fall back to site opening hours
      return null
    },
    computeSlotsForResource (resource) {
      const bounds = this.resourceEffectiveBounds(resource)

      let openTime, closeTime

      if (bounds === null) {
        // No resource hours defined — use site opening hours
        openTime = this.openingTime
        closeTime = this.closingTime
      } else if (bounds.closed) {
        return []
      } else {
        openTime = moment.tz(`${this.dateString}T${bounds.openTimeStr}`, this.selectedTimeZoneId)
        closeTime = moment.tz(`${this.dateString}T${bounds.closeTimeStr}`, this.selectedTimeZoneId)

        // Clamp resource hours to within site opening hours
        if (this.openingTime && openTime.isBefore(this.openingTime)) openTime = this.openingTime.clone()
        if (this.closingTime && closeTime.isAfter(this.closingTime)) closeTime = this.closingTime.clone()
      }

      if (!openTime || !closeTime || !openTime.isBefore(closeTime)) return []

      const resourceAppointments = this.appointments.filter(a => a.resourceId === resource.id)

      const slots = []
      let currentStart = openTime.clone()

      while (true) {
        const currentEnd = currentStart.clone().add(this.duration, 'minutes')

        if (currentEnd.isAfter(closeTime)) break

        // Find all appointments conflicting with this slot and jump to the latest end
        const conflictingEnd = resourceAppointments.reduce((latestEnd, appt) => {
          const apptStart = new Date(appt.startUtc).getTime()
          const apptEnd = new Date(appt.endUtc).getTime()
          const overlaps = apptStart < currentEnd.valueOf() && currentStart.valueOf() < apptEnd
          return overlaps && apptEnd > latestEnd ? apptEnd : latestEnd
        }, 0)

        if (conflictingEnd > 0) {
          currentStart = moment(conflictingEnd)
        } else {
          slots.push({
            id: Math.random().toString(36).substring(2, 10),
            startTime: currentStart.format(),
            endTime: currentEnd.format(),
            status: { loading: false, available: true }
          })
          currentStart = currentEnd
        }
      }

      return slots
    }
  },
  components: {
    'resource-timeslots-item': ResourceTimeSlotsItem
  }
}
</script>

<style scoped>
@media (max-width: 768px) {
  .timeslots-wrapper {
    min-height: 10rem !important
  }
}
</style>
